import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { useNavigate } from 'react-router-dom';
import AddFamilyForm from './AddFamilyForm.tsx';
import EditFamilyForm from './EditFamilyForm.tsx';

interface RanchGirl {
  id: string;
  firstName: string;
  lastName: string;
  age: number;
  skillLevel: string;
  horseAssigned: string;
  isActive: boolean;
}

interface Family {
  id: string;
  familyName: string;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  secondaryContactFirstName?: string;
  secondaryContactLastName?: string;
  secondaryContactEmail?: string;
  secondaryContactPhone?: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  membershipStatus: string;
  monthlyFee: number;
  ranchGirls: RanchGirl[];
  ranchGirlCount: number;
  userId?: string;
}

export default function FamilyManagement() {
  const [families, setFamilies] = useState<Family[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedFamilies, setSelectedFamilies] = useState<Set<string>>(new Set());
  const { user, startImpersonation } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchFamilies();
  }, []);

  const fetchFamilies = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setFamilies(data.data);
      }
    } catch (error) {
      console.error('Error fetching families:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (families.length === 0) {
      alert('No family data to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'Family Name',
      'Primary Contact First Name',
      'Primary Contact Last Name',
      'Primary Contact Email',
      'Primary Contact Phone',
      'Secondary Contact First Name',
      'Secondary Contact Last Name',
      'Secondary Contact Email',
      'Secondary Contact Phone',
      'Address',
      'City',
      'State',
      'ZIP Code',
      'Membership Status',
      'Monthly Fee',
      'Ranch Girls Count',
      'Ranch Girls Names'
    ];

    // Convert families data to CSV rows
    const csvRows = families.map(family => [
      family.familyName,
      family.primaryContactFirstName,
      family.primaryContactLastName,
      family.primaryContactEmail,
      family.primaryContactPhone,
      family.secondaryContactFirstName || '',
      family.secondaryContactLastName || '',
      family.secondaryContactEmail || '',
      family.secondaryContactPhone || '',
      family.address,
      family.city,
      family.state,
      family.zipCode,
      family.membershipStatus,
      family.monthlyFee,
      family.ranchGirlCount,
      family.ranchGirls ? family.ranchGirls.map(girl => `${girl.firstName} ${girl.lastName}`).join('; ') : ''
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `GGAA_Families_Export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const toggleFamilySelection = (familyId: string) => {
    const newSelection = new Set(selectedFamilies);
    if (newSelection.has(familyId)) {
      newSelection.delete(familyId);
    } else {
      newSelection.add(familyId);
    }
    setSelectedFamilies(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedFamilies.size === families.length) {
      setSelectedFamilies(new Set());
    } else {
      setSelectedFamilies(new Set(families.map(f => f.id)));
    }
  };

  const handleMassDelete = async () => {
    if (selectedFamilies.size === 0) {
      alert('Please select families to delete');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedFamilies.size} ${selectedFamilies.size === 1 ? 'family' : 'families'}? This will also delete all associated ranch girls and user accounts. This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/families/bulk-delete', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          familyIds: Array.from(selectedFamilies)
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert(`Successfully deleted ${selectedFamilies.size} ${selectedFamilies.size === 1 ? 'family' : 'families'}`);
        setSelectedFamilies(new Set());
        fetchFamilies();
      } else {
        alert('Error deleting families: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting families:', error);
      alert('Error deleting families');
    }
  };

  const handleLoginAsFamily = async (family: Family) => {
    if (!family.userId) return;
    if (window.confirm(`Log in as ${family.familyName}?`)) {
      const success = await startImpersonation(family.userId);
      if (success) {
        navigate('/dashboard');
      } else {
        window.alert('Failed to impersonate user');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-ggaa-green"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-10">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Family Management</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
            Manage all GGAA families and their information
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3 w-full">
          {selectedFamilies.size > 0 && (
            <button
              onClick={handleMassDelete}
              className="w-full sm:w-auto bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center min-h-[44px]"
            >
              <svg className="w-4 h-4 mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Selected ({selectedFamilies.size})
            </button>
          )}
          <button
            onClick={exportToCSV}
            className="w-full sm:w-auto bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center min-h-[44px]"
          >
            <svg className="w-4 h-4 mr-2 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Export CSV
          </button>
          <button
            onClick={() => setShowAddForm(true)}
            className="w-full sm:w-auto bg-ggaa-green text-white px-4 py-3 rounded-lg hover:bg-ggaa-green/90 transition-colors font-medium min-h-[44px]"
          >
            + Add New Family
          </button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20">
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">Total Families</h3>
          <p className="text-2xl sm:text-3xl font-bold text-ggaa-green">{families.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20">
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">Active Families</h3>
          <p className="text-2xl sm:text-3xl font-bold text-green-600">
            {families.filter(f => f.membershipStatus === 'active').length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20">
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">Total Ranch Girls</h3>
          <p className="text-2xl sm:text-3xl font-bold text-purple-600">
            {families.reduce((sum, f) => sum + f.ranchGirlCount, 0)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-ggaa-green/20 col-span-2 md:col-span-1">
          <h3 className="text-xs sm:text-sm font-medium text-gray-500">Monthly Revenue</h3>
          <p className="text-2xl sm:text-3xl font-bold text-blue-600">
            ${families.reduce((sum, f) => sum + (f.monthlyFee || 0), 0).toFixed(0)}
          </p>
        </div>
      </div>

      {/* Families list */}
      <div className="bg-white rounded-xl shadow-lg border border-ggaa-green/20 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">All Families</h2>
          {families.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-gray-600 lg:hidden min-h-[44px]">
              <input
                type="checkbox"
                checked={selectedFamilies.size === families.length}
                onChange={toggleSelectAll}
                className="h-5 w-5 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
              />
              Select all
              {selectedFamilies.size > 0 && (
                <span className="text-gray-500">({selectedFamilies.size} selected)</span>
              )}
            </label>
          )}
        </div>

        {families.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500">
            <p className="mb-4">No families yet.</p>
            <button
              type="button"
              onClick={() => setShowAddForm(true)}
              className="text-ggaa-green font-medium hover:underline"
            >
              Add your first family
            </button>
          </div>
        ) : (
          <>
        {/* Mobile card view */}
        <div className="lg:hidden divide-y divide-gray-100">
          {families.map((family) => (
            <div key={family.id} className="p-4">
              <div className="flex gap-3 items-start">
                <input
                  type="checkbox"
                  checked={selectedFamilies.has(family.id)}
                  onChange={() => toggleFamilySelection(family.id)}
                  className="mt-1 h-5 w-5 shrink-0 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                  aria-label={`Select ${family.familyName}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <h3 className="text-base font-semibold text-gray-900 break-words">{family.familyName}</h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full shrink-0 ${getStatusColor(family.membershipStatus)}`}>
                      {family.membershipStatus}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">
                    {family.primaryContactFirstName} {family.primaryContactLastName}
                  </p>
                  <div className="mt-2 space-y-1">
                    <a
                      href={`mailto:${family.primaryContactEmail}`}
                      className="block text-sm text-ggaa-green break-all"
                    >
                      {family.primaryContactEmail}
                    </a>
                    <a
                      href={`tel:${family.primaryContactPhone}`}
                      className="block text-sm text-gray-600"
                    >
                      {family.primaryContactPhone}
                    </a>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                    <span>
                      {family.ranchGirlCount} Ranch Girl{family.ranchGirlCount !== 1 ? 's' : ''}
                      {family.ranchGirls && family.ranchGirls.length > 0 && (
                        <span className="text-gray-500"> ({family.ranchGirls.map(girl => girl.firstName).join(', ')})</span>
                      )}
                    </span>
                    <span className="font-medium text-gray-900">${family.monthlyFee}/mo</span>
                  </div>
                  <div className="mt-4 flex flex-col sm:flex-row gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedFamily(family)}
                      className="flex-1 bg-ggaa-green text-white px-4 py-3 rounded-lg hover:bg-ggaa-green/90 text-sm font-medium min-h-[44px]"
                    >
                      Edit
                    </button>
                    {user?.isSuperAdmin && family.userId && (
                      <button
                        type="button"
                        onClick={() => handleLoginAsFamily(family)}
                        className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 text-sm font-medium min-h-[44px]"
                      >
                        Login as family
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedFamilies.size === families.length && families.length > 0}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Family
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Primary Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ranch Girls
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Monthly Fee
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {families.map((family) => (
                <tr key={family.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedFamilies.has(family.id)}
                      onChange={() => toggleFamilySelection(family.id)}
                      className="h-4 w-4 text-ggaa-green focus:ring-ggaa-green border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{family.familyName}</div>
                    <div className="text-sm text-gray-500">{family.primaryContactEmail}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {family.primaryContactFirstName} {family.primaryContactLastName}
                    </div>
                    <div className="text-sm text-gray-500">{family.primaryContactPhone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900">
                        {family.ranchGirlCount} Ranch Girl{family.ranchGirlCount !== 1 ? 's' : ''}
                      </span>
                      {family.ranchGirls && family.ranchGirls.length > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          {family.ranchGirls.map(girl => girl.firstName).join(', ')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(family.membershipStatus)}`}>
                      {family.membershipStatus}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ${family.monthlyFee}/month
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-3">
                    <button
                      onClick={() => setSelectedFamily(family)}
                      className="text-ggaa-green hover:text-ggaa-green/80"
                    >
                      Edit
                    </button>
                    {user?.isSuperAdmin && family.userId && (
                      <button
                        type="button"
                        onClick={() => handleLoginAsFamily(family)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Login as this family to troubleshoot"
                      >
                        🎭 Login As
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          </>
        )}
      </div>

      {/* Add Family Modal */}
      {showAddForm && (
        <AddFamilyForm
          onClose={() => setShowAddForm(false)}
          onSuccess={() => {
            setShowAddForm(false);
            fetchFamilies();
          }}
        />
      )}

      {/* Edit Family Modal */}
      {selectedFamily && (
        <EditFamilyForm
          family={selectedFamily}
          onClose={() => setSelectedFamily(null)}
          onSuccess={() => {
            setSelectedFamily(null);
            fetchFamilies();
          }}
        />
      )}
    </div>
  );
} 